import { combineLatest, of } from 'rxjs'
import { map } from 'rxjs/operators'
import dayjs from 'dayjs'
import produce from 'immer'
import { result } from 'libraries/lodash'
import debug from 'front-end-core/core/debug'
import { RESPONSE_BADDATA } from 'front-end-core/constants/responses'
import { mapResponse } from 'front-end-core/helpers/dataflow'
import {
  QA_SEARCH,
  QA_SEARCH_PREPAY,
  QA_SECTIONS,
  QA_SECTIONS_PREPAY,
  LEGISLATION_SEARCH,
  LEGISLATION_SEARCH_PREPAY,
  CURRENT_AWARENESS_SEARCH,
  CURRENT_AWARENESS_SEARCH_PREPAY,
  METADATA_SEARCH,
  METADATA_SEARCH_PREPAY
} from 'constants/requests'
import { DEFAULT_FLAVOUR } from 'constants/flavours'
import { fetch } from 'services/ApiService'
import loggedRequestSwitcher from 'helpers/loggedRequestSwitcher'
import { mapDate } from './CurrentAwarenessService'
import { DATE_FORMAT, TIME_FORMAT } from 'constants/locale'

export const addPrecedentPaths = function (search, sections) {
  const getTitle = (id) => result(sections, ['nodes', id, 'title'], null)
  const results = search.results
    .map(p => ({
      ...p,
      parentTitle: getTitle(p.parentId),
      sectionId: result(sections, ['nodes', p.parentId, 'path', 0], null),
      sectionTitle: getTitle(result(sections, ['nodes', p.parentId, 'path', 0]))
    }))

  return { ...search, results }
}

const addQaPaths = function (search, sections) {
  try {
    const getPathElement = (id) => ({ id, title: sections.nodes[id].title })
    const getPath = (id) => sections.nodes[id].path.map(getPathElement)
    // Filter out result if we can't find the path
    const results = search.results.reduce((acc, curr) => {
      try {
        const path = getPath(curr.sectionId)
        return [...acc, { ...curr, path }]
      } catch (error) {
        return acc
      }
    }, [])

    return { ...search, results }
  } catch (e) {
    debug.error("Search results parent ids don't match section ids.")
    return RESPONSE_BADDATA
  }
}

const requestQASearchResults = (aol, flavour, query, queryString, init, limit) => {
  const loggedRequest = () => combineLatest(
    [fetch(QA_SEARCH, { aol, flavour }, { ...query, q: queryString, i: init, s: limit }),
      fetch(QA_SECTIONS, { aol, flavour })],
    mapResponse(addQaPaths)
  )
  const prepayRequest = () => combineLatest(
    [fetch(QA_SEARCH_PREPAY, { flavour, aol }, { ...query, q: queryString, i: init, s: limit }),
      fetch(QA_SECTIONS_PREPAY, { flavour, aol })],
    mapResponse(addQaPaths)
  )
  return loggedRequestSwitcher(loggedRequest, prepayRequest)
}

const requestLegislationSearchResults = ({ params, query }) => {
  const loggedRequest = () => fetch(LEGISLATION_SEARCH, params, query)
  const prepayRequest = () => fetch(LEGISLATION_SEARCH_PREPAY, params, query)
  return loggedRequestSwitcher(loggedRequest, prepayRequest)
}

const requestPrecedentsSearchResults = ({ params = {}, searchQuery }) => {
  const loggedRequest = () => combineLatest(
    [fetch(METADATA_SEARCH, params, searchQuery),
      fetch(QA_SECTIONS, { ...params, flavour: DEFAULT_FLAVOUR })],
    mapResponse(addPrecedentPaths)
  )
  const prepayRequest = () => combineLatest(
    [fetch(METADATA_SEARCH_PREPAY, params, searchQuery),
      fetch(QA_SECTIONS_PREPAY, { flavour: DEFAULT_FLAVOUR, aol: params.aol })],
    mapResponse(addPrecedentPaths)
  )
  return loggedRequestSwitcher(loggedRequest, prepayRequest)
}

const mapPublicationDates = resp => {
  return produce(resp, draft => {
    draft.results = draft.results.map(result => {
      return produce(result, draft => {
        draft.dateOfPublication = mapDate(draft.dateOfPublication)
      })
    })
  })
}

const requestCurrentAwarenessSearchResults = ({ params, fromDate, toDate, aol }) => {
  const urlVars = {
    fromDate: fromDate && toDate ? fromDate : null,
    toDate: fromDate && toDate ? toDate : null,
    search: fromDate && toDate ? null : 'search',
    aol
  }
  const loggedRequest = () => fetch(CURRENT_AWARENESS_SEARCH, urlVars, params)
  const prepayRequest = () => fetch(CURRENT_AWARENESS_SEARCH_PREPAY, urlVars, params)
  return loggedRequestSwitcher(loggedRequest, prepayRequest).pipe(
    map(mapResponse(mapPublicationDates))
  )
}

export const requestSearchResults = ({ searchPhrase: queryString, searchType, pageNumber, pageLimit, aol, flavour, query }) => {
  const limit = +pageLimit
  const init = ((+pageNumber || 1) - 1) * limit + 1

  switch (searchType) {
    case 'qa':
      return requestQASearchResults(aol, flavour, query, queryString, init, limit)
    case 'legislation':
      return requestLegislationSearchResults(({ params: { aol, queryString, limit, init }, query: { q: queryString, i: init, s: limit } }))
    case 'precedents':
      return requestPrecedentsSearchResults({
        params: { aol },
        searchQuery: {
          search: queryString,
          limit,
          init: init - 1,
          query: query.library && `library=${query.library}`,
          automated: query.automated
        }
      })
    case 'ca':
      return requestCurrentAwarenessSearchResults({
        params: { page: pageNumber || 1, size: pageLimit, format: 'slim', q: queryString, chronological: query.sort !== 'Relevance' },
        fromDate: query.fromDate ? dayjs(query.fromDate).format(`${DATE_FORMAT}T${TIME_FORMAT}`) : null,
        toDate: query.toDate ? dayjs(query.toDate).format(`${DATE_FORMAT}T${TIME_FORMAT}`) : null,
        aol
      })
    default:
      return of(RESPONSE_BADDATA)
  }
}
